
import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ThumbsUp, ThumbsDown, Bookmark, BookmarkCheck, AlertCircle, CheckCircle } from "lucide-react";

interface DocumentationProps {
  activePage: string;
  feedbackSubmitted: Record<string, boolean>;
  bookmarkedArticles: string[];
  submitFeedback: (articleId: string, isHelpful: boolean) => void;
  toggleBookmark: (articleId: string) => void;
}

export const ReleaseNotesContent: React.FC<DocumentationProps> = ({
  activePage,
  feedbackSubmitted,
  bookmarkedArticles,
  submitFeedback,
  toggleBookmark
}) => {
  const articleId = `release-notes-${activePage}`;
  const isBookmarked = bookmarkedArticles.includes(articleId);
  const hasFeedback = feedbackSubmitted[articleId];

  return (
    <Tabs defaultValue={activePage} className="w-full">
      <TabsList className="w-full mb-4 overflow-x-auto flex flex-nowrap justify-start">
        <TabsTrigger value="latest-release">Latest Release</TabsTrigger>
        <TabsTrigger value="previous-releases">Previous Releases</TabsTrigger>
        <TabsTrigger value="upcoming-features">Upcoming Features</TabsTrigger>
        <TabsTrigger value="deprecations">Deprecations</TabsTrigger>
        <TabsTrigger value="migration-guides">Migration Guides</TabsTrigger>
      </TabsList>

      {/* Latest Release */}
      <TabsContent value="latest-release" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-1">DGK Platform v3.2.0</h2>
            <p className="text-sm text-muted-foreground">Released: June 15, 2023</p>
          </div>
          <Badge className="bg-emerald-500">Current Version</Badge>
        </div>
        
        <Separator />
        
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium mb-3">What's New</h3>
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                      Feature
                    </Badge>
                    <CardTitle className="text-md">Enhanced AI Job Matching</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p>Our AI job matching algorithm has been completely revamped with improved skills assessment, project history analysis, and client preference matching. Users are now experiencing a 45% increase in successful matches compared to the previous version.</p>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <img src="/placeholder.svg" alt="AI Job Matching - Before" className="rounded-md border" />
                    <img src="/placeholder.svg" alt="AI Job Matching - After" className="rounded-md border" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-200">
                      Enhancement
                    </Badge>
                    <CardTitle className="text-md">Team Builder 2.0</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p>The Team Builder feature now offers intelligent team composition suggestions based on project requirements, skill compatibility, and past collaboration history. New team templates are available for common project types, and you can save custom team configurations for future use.</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">
                      Performance
                    </Badge>
                    <CardTitle className="text-md">Platform-wide Performance Improvements</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p>We've reduced page load times by an average of 40% across the platform through code optimization, better image compression, and implementing advanced caching strategies. The dashboard now loads in under 2 seconds, even on slower connections.</p>
                </CardContent>
              </Card>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-medium mb-3">Bug Fixes</h3>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="bug-1">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">
                      Critical
                    </Badge>
                    <span>Fixed payment processing errors during high traffic periods</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  We've completely rebuilt our payment processing infrastructure to handle concurrent transactions more efficiently. This resolves the intermittent errors some users were experiencing during peak platform usage times.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="bug-2">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-orange-50 text-orange-600 border-orange-200">
                      Major
                    </Badge>
                    <span>Resolved notifications delivery delays</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  The notification system has been optimized to ensure real-time delivery, even during heavy platform usage. We've implemented a new queuing system that prioritizes time-sensitive notifications like payment confirmations and meeting reminders.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="bug-3">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">
                      Minor
                    </Badge>
                    <span>Fixed display issues on mobile devices</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  We've addressed several UI inconsistencies on mobile devices, particularly on the profile and project details pages. All charts and graphs are now properly responsive and provide meaningful information even on smaller screens.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
          
          <div>
            <h3 className="text-lg font-medium mb-3">Security Updates</h3>
            <Card className="bg-gray-50 border-gray-200 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium mb-1">Important Security Enhancements</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      This release includes critical security updates to the authentication system and data encryption protocols. All users will be prompted to reset their passwords upon login.
                    </p>
                    <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                      <li>Updated to the latest encryption standards for all stored data</li>
                      <li>Implemented additional verification steps for password changes</li>
                      <li>Enhanced protection against cross-site scripting attacks</li>
                      <li>Improved session management to prevent unauthorized access</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-6">
          <h3 className="font-medium mb-2">Was this release note helpful?</h3>
          {!hasFeedback ? (
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => submitFeedback(articleId, true)}
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it was helpful
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => submitFeedback(articleId, false)}
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, it needs improvement
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => toggleBookmark(articleId)}
                className={isBookmarked ? "text-blue-600" : ""}
              >
                {isBookmarked ? (
                  <BookmarkCheck className="h-4 w-4 mr-2" />
                ) : (
                  <Bookmark className="h-4 w-4 mr-2" />
                )}
                {isBookmarked ? "Bookmarked" : "Bookmark"}
              </Button>
            </div>
          ) : (
            <div className="flex items-center text-green-600">
              <CheckCircle className="h-4 w-4 mr-2" />
              <span>Thank you for your feedback!</span>
            </div>
          )}
        </div>
      </TabsContent>

      {/* Previous Releases */}
      <TabsContent value="previous-releases" className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold mb-4">Previous Releases</h2>
          
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="v3.1.0">
              <AccordionTrigger>
                <div className="flex items-center justify-between w-full">
                  <div className="font-medium">Version 3.1.0</div>
                  <Badge variant="outline" className="ml-auto mr-4">May 2, 2023</Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-2">
                  <div>
                    <h4 className="font-medium mb-2">New Features</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li>Introduced AI Agents for automated task handling</li>
                      <li>Added contract templates for common project types</li>
                      <li>Launched the integrated messaging system with file sharing</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-2">Improvements</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li>Redesigned the dashboard for better data visualization</li>
                      <li>Improved search functionality with advanced filters</li>
                      <li>Enhanced mobile responsiveness across all pages</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-2">Bug Fixes</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li>Fixed calendar synchronization issues</li>
                      <li>Resolved notification duplication problem</li>
                      <li>Fixed profile image upload errors on certain browsers</li>
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="v3.0.0">
              <AccordionTrigger>
                <div className="flex items-center justify-between w-full">
                  <div className="font-medium">Version 3.0.0</div>
                  <Badge variant="outline" className="ml-auto mr-4">March 15, 2023</Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-2">
                  <div>
                    <h4 className="font-medium mb-2">Major Platform Update</h4>
                    <p className="text-gray-700 mb-2">
                      Version 3.0.0 represents a complete platform redesign with a focus on performance, usability, and new capabilities.
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li>Complete UI/UX redesign with improved accessibility</li>
                      <li>New blockchain-based verification system for credentials</li>
                      <li>Integrated payment system with multiple currency support</li>
                      <li>Comprehensive analytics dashboard for freelancers and clients</li>
                      <li>Advanced project management tools with Gantt charts</li>
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="v2.5.0">
              <AccordionTrigger>
                <div className="flex items-center justify-between w-full">
                  <div className="font-medium">Version 2.5.0</div>
                  <Badge variant="outline" className="ml-auto mr-4">January 10, 2023</Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-2">
                  <div>
                    <h4 className="font-medium mb-2">Key Features</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li>Introduced the digital portfolio system</li>
                      <li>Added skills testing and certification</li>
                      <li>Launched the marketplace for digital services</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-2">Improvements & Fixes</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li>Improved search algorithm for better job matching</li>
                      <li>Fixed numerous UI inconsistencies</li>
                      <li>Enhanced mobile app performance</li>
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
        
        <Button variant="outline" className="w-full">
          Load More Previous Releases
        </Button>
        
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-6">
          <h3 className="font-medium mb-2">Was this release history helpful?</h3>
          {!hasFeedback ? (
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => submitFeedback(articleId, true)}
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it was helpful
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => submitFeedback(articleId, false)}
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, it needs improvement
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => toggleBookmark(articleId)}
                className={isBookmarked ? "text-blue-600" : ""}
              >
                {isBookmarked ? (
                  <BookmarkCheck className="h-4 w-4 mr-2" />
                ) : (
                  <Bookmark className="h-4 w-4 mr-2" />
                )}
                {isBookmarked ? "Bookmarked" : "Bookmark"}
              </Button>
            </div>
          ) : (
            <div className="flex items-center text-green-600">
              <CheckCircle className="h-4 w-4 mr-2" />
              <span>Thank you for your feedback!</span>
            </div>
          )}
        </div>
      </TabsContent>

      {/* Upcoming Features */}
      <TabsContent value="upcoming-features" className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold mb-4">Upcoming Features</h2>
          <p className="text-gray-600 mb-6">
            Here's a preview of features and improvements we're currently working on for future releases. 
            Please note that development timelines may change based on feedback and priorities.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Blockchain Payment Integration</CardTitle>
                  <Badge className="bg-purple-500">Q3 2023</Badge>
                </div>
                <CardDescription>Secure cryptocurrency payments for global transactions</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700">
                  We're integrating multiple cryptocurrency payment options to enable faster, more secure international transactions with lower fees. This will include Bitcoin, Ethereum, and several stablecoins, with automatic conversion to fiat currencies.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Advanced AI Assistant</CardTitle>
                  <Badge className="bg-blue-500">Q3 2023</Badge>
                </div>
                <CardDescription>AI-powered guidance for freelancers and clients</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700">
                  Our new AI assistant will help users optimize their profiles, improve project descriptions, provide market rate suggestions, and offer personalized career development advice based on skills and portfolio analysis.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Enterprise Team Management</CardTitle>
                  <Badge className="bg-emerald-500">Q4 2023</Badge>
                </div>
                <CardDescription>Tools for managing large distributed teams</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700">
                  New enterprise-grade features will include department management, budget allocation tools, approval workflows, compliance monitoring, and detailed reporting with custom dashboards for executives and team leads.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Skill Verification 2.0</CardTitle>
                  <Badge className="bg-amber-500">Q4 2023</Badge>
                </div>
                <CardDescription>Enhanced skill assessment and certification</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700">
                  We're expanding our skill verification system with industry-standard assessments, proctored exams, hands-on project evaluations, and blockchain-verified credentials that can be shared across multiple platforms.
                </p>
              </CardContent>
            </Card>
            
            <Card className="md:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Global Talent Pools</CardTitle>
                  <Badge className="bg-indigo-500">Q1 2024</Badge>
                </div>
                <CardDescription>Pre-vetted talent groups for specific industries</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700 mb-4">
                  We're creating curated talent pools for specialized industries, with freelancers who have undergone rigorous verification, skills assessment, and background checks. These pools will enable faster team assembly for enterprise clients with specific industry needs.
                </p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-gray-50 p-2 rounded border border-gray-200">
                    <p className="font-medium text-sm">Healthcare Tech</p>
                    <p className="text-xs text-gray-500">HIPAA Compliant</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded border border-gray-200">
                    <p className="font-medium text-sm">Financial Services</p>
                    <p className="text-xs text-gray-500">SOC 2 Certified</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded border border-gray-200">
                    <p className="font-medium text-sm">Government</p>
                    <p className="text-xs text-gray-500">Security Cleared</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-6">
          <h3 className="font-medium mb-2">Was this roadmap helpful?</h3>
          {!hasFeedback ? (
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => submitFeedback(articleId, true)}
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it was helpful
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => submitFeedback(articleId, false)}
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, it needs improvement
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => toggleBookmark(articleId)}
                className={isBookmarked ? "text-blue-600" : ""}
              >
                {isBookmarked ? (
                  <BookmarkCheck className="h-4 w-4 mr-2" />
                ) : (
                  <Bookmark className="h-4 w-4 mr-2" />
                )}
                {isBookmarked ? "Bookmarked" : "Bookmark"}
              </Button>
            </div>
          ) : (
            <div className="flex items-center text-green-600">
              <CheckCircle className="h-4 w-4 mr-2" />
              <span>Thank you for your feedback!</span>
            </div>
          )}
        </div>
      </TabsContent>

      {/* Deprecations */}
      <TabsContent value="deprecations" className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold mb-4">Deprecations & End of Life</h2>
          <p className="text-gray-600 mb-6">
            This page contains information about features and APIs that are being deprecated or have reached end of life.
            Please review this information to ensure a smooth transition to newer alternatives.
          </p>
          
          <div className="space-y-4">
            <Card className="border-amber-200">
              <CardHeader className="bg-amber-50 border-b border-amber-200">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-amber-800">Legacy API (v1) Deprecation</CardTitle>
                  <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">
                    December 31, 2023
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-gray-700 mb-4">
                  The v1 API will be fully deprecated by the end of this year. All API consumers should migrate to the v2 API, 
                  which offers improved performance, security, and additional features.
                </p>
                
                <div className="bg-gray-50 p-3 rounded border border-gray-200 mb-4">
                  <h4 className="font-medium mb-2">Migration Steps</h4>
                  <ol className="list-decimal pl-5 space-y-1 text-sm text-gray-700">
                    <li>Update your API client to use the v2 endpoint (api.dgkplatform.com/v2/)</li>
                    <li>Regenerate API keys in the Developer Console</li>
                    <li>Update request and response handling per the v2 documentation</li>
                    <li>Test thoroughly in the sandbox environment</li>
                  </ol>
                </div>
                
                <Button variant="outline" size="sm">
                  View Migration Guide
                </Button>
              </CardContent>
            </Card>
            
            <Card className="border-red-200">
              <CardHeader className="bg-red-50 border-b border-red-200">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-red-800">Old Marketplace Payment System</CardTitle>
                  <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50">
                    September 30, 2023
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-gray-700 mb-4">
                  The legacy payment processing system used in the marketplace will be discontinued. 
                  All transactions will be processed through our new secure payment platform with enhanced fraud protection.
                </p>
                
                <div className="bg-red-50 p-3 rounded border border-red-200 mb-4">
                  <h4 className="font-medium text-red-800 mb-2">Important Action Required</h4>
                  <p className="text-sm text-gray-700">
                    All users must update their payment information in the account settings before September 30, 2023. 
                    Any recurring payments will need to be reconfigured in the new system.
                  </p>
                </div>
                
                <Button variant="outline" size="sm">
                  Update Payment Information
                </Button>
              </CardContent>
            </Card>
            
            <Card className="border-gray-200">
              <CardHeader className="bg-gray-50 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-gray-800">Legacy Mobile App (v2.x)</CardTitle>
                  <Badge variant="outline" className="border-gray-300 text-gray-700 bg-gray-50">
                    Already Deprecated
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-gray-700 mb-4">
                  The v2.x mobile applications for iOS and Android are no longer supported. 
                  Users should update to the latest version (v3.x) from the App Store or Google Play.
                </p>
                
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm">
                    Download iOS App
                  </Button>
                  <Button variant="outline" size="sm">
                    Download Android App
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-6">
          <h3 className="font-medium mb-2">Was this deprecation information helpful?</h3>
          {!hasFeedback ? (
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => submitFeedback(articleId, true)}
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it was helpful
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => submitFeedback(articleId, false)}
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, it needs improvement
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => toggleBookmark(articleId)}
                className={isBookmarked ? "text-blue-600" : ""}
              >
                {isBookmarked ? (
                  <BookmarkCheck className="h-4 w-4 mr-2" />
                ) : (
                  <Bookmark className="h-4 w-4 mr-2" />
                )}
                {isBookmarked ? "Bookmarked" : "Bookmark"}
              </Button>
            </div>
          ) : (
            <div className="flex items-center text-green-600">
              <CheckCircle className="h-4 w-4 mr-2" />
              <span>Thank you for your feedback!</span>
            </div>
          )}
        </div>
      </TabsContent>

      {/* Migration Guides */}
      <TabsContent value="migration-guides" className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold mb-4">Migration Guides</h2>
          <p className="text-gray-600 mb-6">
            These guides will help you migrate from older versions of the platform to the latest release.
            Follow the step-by-step instructions to ensure a smooth transition.
          </p>
          
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Migrating from v2.x to v3.x</CardTitle>
                <CardDescription>Complete platform migration guide</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4">
                  Version 3.x introduces a new architecture and several breaking changes. 
                  This comprehensive guide covers all aspects of migration for both individual users and enterprise customers.
                </p>
                
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="section-1">
                    <AccordionTrigger>1. Account and Authentication Changes</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <p className="text-sm text-gray-700">
                          The authentication system has been completely revamped with improved security features:
                        </p>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                          <li>All users need to reset their passwords upon first login to v3</li>
                          <li>Two-factor authentication is now mandatory for all accounts</li>
                          <li>API keys need to be regenerated in the new Developer Console</li>
                          <li>Session management has changed with shorter token expiration times</li>
                        </ul>
                        <div className="bg-blue-50 p-3 rounded border border-blue-200 mt-2">
                          <p className="text-sm text-blue-700">
                            <strong>Tip:</strong> Before migrating, export your account data from v2.x using the Account Export tool to ensure you have a backup.
                          </p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  
                  <AccordionItem value="section-2">
                    <AccordionTrigger>2. API Changes and Endpoints</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <p className="text-sm text-gray-700">
                          The API structure has been redesigned to be more RESTful and consistent:
                        </p>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                          <li>Base URL has changed to api.dgkplatform.com/v3/</li>
                          <li>Response formats have been standardized across all endpoints</li>
                          <li>Pagination parameters are now consistent (page and per_page)</li>
                          <li>New rate limiting headers have been implemented</li>
                        </ul>
                        <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded font-mono text-xs overflow-auto">
                          <p className="text-gray-700">// Example v3 API request</p>
                          <p className="text-green-600">GET api.dgkplatform.com/v3/projects</p>
                          <p className="text-gray-700">Headers:</p>
                          <p className="text-blue-600">Authorization: Bearer {'<your-token>'}</p>
                          <p className="text-blue-600">Accept: application/json</p>
                          <p className="text-blue-600">Content-Type: application/json</p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  
                  <AccordionItem value="section-3">
                    <AccordionTrigger>3. Data Migration Process</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <p className="text-sm text-gray-700">
                          Follow these steps to migrate your data to the new platform:
                        </p>
                        <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-700">
                          <li>
                            <strong>Export Data:</strong> Use the Data Export tool in your v2.x account settings to download all your data
                          </li>
                          <li>
                            <strong>Verify Account:</strong> Ensure your email is verified on the new platform
                          </li>
                          <li>
                            <strong>Import Data:</strong> Use the Data Import wizard in v3.x to upload your exported files
                          </li>
                          <li>
                            <strong>Verify Migration:</strong> Check that all projects, profiles, and settings have been correctly transferred
                          </li>
                          <li>
                            <strong>Reconfigure Integrations:</strong> Set up any third-party integrations again using the new API endpoints
                          </li>
                        </ol>
                        <div className="bg-amber-50 p-3 rounded border border-amber-200 mt-2">
                          <p className="text-sm text-amber-700">
                            <strong>Important:</strong> The data migration tool will be available until March 31, 2024. After this date, automatic migration will no longer be supported.
                          </p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                
                <div className="mt-4">
                  <Button className="w-full">
                    Download Complete Migration Guide (PDF)
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>API v1 to v2 Migration</CardTitle>
                <CardDescription>Technical guide for developers</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4">
                  This guide is specifically for developers who need to update their integrations from the v1 API to the v2 API.
                </p>
                
                <div className="bg-gray-50 p-4 rounded border border-gray-200 mb-4">
                  <h4 className="font-medium mb-2">Key Changes</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                    <li>Authentication now uses OAuth 2.0 instead of API keys</li>
                    <li>JSON is now the only supported response format</li>
                    <li>Webhooks use a new signature verification method</li>
                    <li>Error responses follow RFC 7807 Problem Details standard</li>
                  </ul>
                </div>
                
                <Button variant="outline" className="w-full">
                  View API Migration Documentation
                </Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Enterprise Migration Checklist</CardTitle>
                <CardDescription>For enterprise administrators</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4">
                  A comprehensive checklist for enterprise administrators to ensure a smooth transition to the latest platform version.
                </p>
                
                <div className="space-y-2">
                  {[
                    "Inform all users about the migration timeline",
                    "Export all company data and reports",
                    "Schedule training sessions for new features",
                    "Update SSO configuration",
                    "Reconfigure compliance settings",
                    "Test user permissions and access controls",
                    "Verify integration with internal systems",
                    "Update billing information and subscription details"
                  ].map((item, index) => (
                    <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                      <Checkbox id={`checklist-${index}`} />
                      <label htmlFor={`checklist-${index}`} className="text-sm text-gray-700">
                        {item}
                      </label>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4">
                  <Button variant="outline" className="w-full">
                    Download Enterprise Migration Guide
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-6">
          <h3 className="font-medium mb-2">Was this migration guide helpful?</h3>
          {!hasFeedback ? (
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => submitFeedback(articleId, true)}
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it was helpful
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => submitFeedback(articleId, false)}
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, it needs improvement
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => toggleBookmark(articleId)}
                className={isBookmarked ? "text-blue-600" : ""}
              >
                {isBookmarked ? (
                  <BookmarkCheck className="h-4 w-4 mr-2" />
                ) : (
                  <Bookmark className="h-4 w-4 mr-2" />
                )}
                {isBookmarked ? "Bookmarked" : "Bookmark"}
              </Button>
            </div>
          ) : (
            <div className="flex items-center text-green-600">
              <CheckCircle className="h-4 w-4 mr-2" />
              <span>Thank you for your feedback!</span>
            </div>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
};

// Add missing import for Checkbox component
import { Checkbox } from "@/components/ui/checkbox";
